import { useState } from "react";
import { useNavigate } from "@entry/navigation";

import { ErrorComponent } from "@components/ErrorComponent";
import { GlassInput, GlassSelect } from "@entry/ui";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { AccountProvider, Routes, UserEmploymentStatus } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import {
  useBusinessAddress,
  useErrorBanner,
  useFetchPlaceId,
  useIndustries,
  useOccupations,
  useOnboarding,
  useSetUserFinancialProfile,
  useUser,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { requiresIndustryForAltruist } from "@utils";

const options = [
  i18n.t(Translations.EmploymentRouteEmployed),
  i18n.t(Translations.EmploymentRouteSelfEmployed),
  i18n.t(Translations.EmploymentRouteRetired),
  i18n.t(Translations.EmploymentRouteUnemployed),
  i18n.t(Translations.EmploymentRouteStudent),
  i18n.t(Translations.EmploymentRouteHomeMaker),
];

export const EmploymentRoute = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { navigateToNext, resetToNext } = useOnboarding();

  const {
    keys: industryKeys,
    values: industryValues,
    isEmpty: industryEmpty,
    loading: industriesLoading,
    error: industriesError,
  } = useIndustries();

  const {
    keys: occupationKeys,
    values: occupationValues,
    isEmpty: occupationEmpty,
    loading: occupationsLoading,
    error: occupationsError,
  } = useOccupations();

  const { user } = useUser();

  const [setUserFinancialProfile] = useSetUserFinancialProfile();
  const [fetchBusinessAddress] = useBusinessAddress();
  const [fetchPlaceId] = useFetchPlaceId();

  const { showErrorBanner } = useErrorBanner();

  const [statusIndex, setStatusIndex] = useState<number>();
  const [industryIndex, setIndustryIndex] = useState<number>();
  const [occupationIndex, setOccupationIndex] = useState<number>();

  const [employerName, setEmployerName] = useState<string>();

  const status = (() => {
    if (statusIndex == null) return undefined;

    const value = options[statusIndex];

    switch (value) {
      case i18n.t(Translations.EmploymentRouteEmployed):
        return UserEmploymentStatus.Employed;
      case i18n.t(Translations.EmploymentRouteSelfEmployed):
        return UserEmploymentStatus.SelfEmployed;
      case i18n.t(Translations.EmploymentRouteRetired):
        return UserEmploymentStatus.Retired;
      case i18n.t(Translations.EmploymentRouteUnemployed):
        return UserEmploymentStatus.Unemployed;
      case i18n.t(Translations.EmploymentRouteStudent):
        return UserEmploymentStatus.Student;
      case i18n.t(Translations.EmploymentRouteHomeMaker):
        return UserEmploymentStatus.HomeMaker;
    }
  })();

  const isEmployedStatus = Boolean(
    status && [UserEmploymentStatus.Employed, UserEmploymentStatus.SelfEmployed].includes(status),
  );

  const selectedOccupation = occupationIndex != null ? occupationKeys[occupationIndex] : null;
  const requiresIndustry = requiresIndustryForAltruist(status, selectedOccupation);

  const isValid =
    (status && !isEmployedStatus) ||
    (isEmployedStatus &&
      employerName &&
      occupationIndex != null &&
      (!requiresIndustry || industryIndex != null));

  const loading = industriesLoading || occupationsLoading;
  const error = industriesError || industryEmpty || occupationsError || occupationEmpty;

  const handleSubmit = async () => {
    const industry = requiresIndustry ? industryKeys[industryIndex!] : undefined;
    const occupation = occupationKeys[occupationIndex!];

    try {
      setIsSubmitting(true);

      await setUserFinancialProfile({
        variables: {
          employmentStatus: status,
          employerName,
          occupation,
          industry,
        },
      });

      if (isEmployedStatus && employerName && user?.provider === AccountProvider.Alpaca) {
        const { data } = await fetchBusinessAddress({
          variables: {
            input: employerName,
          },
        });

        if (data?.businessAddress && data?.businessAddress?.city !== "Anytown") {
          const address = data.businessAddress.address ?? "";
          const city = data.businessAddress.city ?? "";
          const state = data.businessAddress.state ?? "";
          const zipCode = data.businessAddress.zip ?? "";

          const fullAddress = [address, city, state, zipCode].join(",");

          const response = await fetchPlaceId(fullAddress);

          if (response) {
            const { placeId, description: fullAddress } = response;

            return navigate(Routes.EmployerAddressAutofill, { state: {
              placeId,
              fullAddress,
            } });
          }
        }
      }

      if (isEmployedStatus) {
        navigateToNext();
      } else {
        resetToNext();
      }
    } catch {
      showErrorBanner();
    } finally {
      setIsSubmitting(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.EmploymentRouteTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.EmploymentRoutePrimaryCTA),
        onClick: handleSubmit,
        disabled: !isValid,
        loading: isSubmitting,
      },
    },
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <OnboardingLayout {...props}>
      <GlassSelect
        label={i18n.t(Translations.EmploymentRouteStatusInputLabel)}
        placeholder={i18n.t(Translations.EmploymentRouteStatusInputPlaceholder)}
        items={options}
        selectedIndex={statusIndex}
        onSelect={setStatusIndex}
      />
      <Spacing size={SpacingSizes.M} />

      {isEmployedStatus ? (
        <>
          <GlassInput
            label={i18n.t(Translations.EmploymentRouteNameInputLabel)}
            placeholder={i18n.t(Translations.EmploymentRouteNameInputPlaceholder)}
            value={employerName ?? ""}
            onChange={(e) => setEmployerName(e.target.value)}
          />
          <Spacing size={SpacingSizes.M} />

          <GlassSelect
            label={i18n.t(Translations.EmploymentRouteOccupationLabel)}
            placeholder={i18n.t(Translations.EmploymentRouteOccupationPlaceholder)}
            items={occupationValues}
            selectedIndex={occupationIndex}
            onSelect={setOccupationIndex}
          />
          <Spacing size={SpacingSizes.M} />

          {requiresIndustry ? (
            <>
              <GlassSelect
                label={i18n.t(Translations.EmploymentRouteIndustryLabel)}
                placeholder={i18n.t(Translations.EmploymentRouteIndustryPlaceholder)}
                items={industryValues}
                selectedIndex={industryIndex}
                onSelect={setIndustryIndex}
              />
              <Spacing size={SpacingSizes.M} />
            </>
          ) : null}
        </>
      ) : null}
    </OnboardingLayout>
  );
};
